import type {
  EnvironmentId,
  ClientOrchestrationShellSnapshot,
  ClientOrchestrationShellStreamEvent,
  ServerConfig,
  ServerLifecycleWelcomePayload,
} from "@t3tools/contracts";

import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";

import { getReconnectDelayMs, DEFAULT_RECONNECT_BACKOFF } from "./reconnectBackoff.ts";
import type { KnownEnvironment } from "./knownEnvironment.ts";
import type { WsRpcClient } from "./wsRpcClient.ts";

export interface EnvironmentConnection {
  readonly kind: "primary" | "saved";
  readonly environmentId: EnvironmentId;
  readonly knownEnvironment: KnownEnvironment;
  readonly client: WsRpcClient;
  readonly ensureBootstrapped: () => Promise<void>;
  readonly reconnect: () => Promise<void>;
  readonly dispose: () => Promise<void>;
}

interface OrchestrationHandlers {
  readonly applyShellEvent: (
    event: ClientOrchestrationShellStreamEvent,
    environmentId: EnvironmentId,
  ) => void;
  readonly syncShellSnapshot: (
    snapshot: ClientOrchestrationShellSnapshot,
    environmentId: EnvironmentId,
  ) => void;
}

export interface EnvironmentConnectionInput extends OrchestrationHandlers {
  readonly kind: "primary" | "saved";
  readonly knownEnvironment: KnownEnvironment;
  readonly client: WsRpcClient;
  readonly fetchShellSnapshot: () => Promise<ClientOrchestrationShellSnapshot>;
  readonly refreshMetadata?: () => Promise<void>;
  readonly onConfigSnapshot?: (config: ServerConfig) => void;
  readonly onWelcome?: (payload: ServerLifecycleWelcomePayload) => void;
  readonly onShellResubscribe?: (environmentId: EnvironmentId) => void;
}

export interface EnvironmentConnectionAttempt {
  readonly environmentId: EnvironmentId;
  readonly isCurrent: () => boolean;
}

export class EnvironmentConnectionAttemptCancelledError extends Error {
  constructor(environmentId: EnvironmentId) {
    super(`Environment connection attempt ${environmentId} was cancelled.`);
    this.name = "EnvironmentConnectionAttemptCancelledError";
  }
}

export function createEnvironmentConnectionAttemptRegistry() {
  const attempts = new Map<EnvironmentId, symbol>();

  return {
    begin: (environmentId: EnvironmentId): EnvironmentConnectionAttempt => {
      const id = Symbol(environmentId);
      attempts.set(environmentId, id);
      return {
        environmentId,
        isCurrent: () => attempts.get(environmentId) === id,
      };
    },
    cancel: (environmentId: EnvironmentId): void => {
      attempts.delete(environmentId);
    },
    clear: (): void => {
      attempts.clear();
    },
  };
}

export class EnvironmentConnectionDisposedError extends Error {
  constructor(environmentId: EnvironmentId) {
    super(`Environment connection ${environmentId} was disposed before it finished bootstrapping.`);
    this.name = "EnvironmentConnectionDisposedError";
  }
}

function createBootstrapGate() {
  let resolve: (() => void) | null = null;
  let reject: ((error: unknown) => void) | null = null;
  const makePromise = () => {
    const nextPromise = new Promise<void>((nextResolve, nextReject) => {
      resolve = nextResolve;
      reject = nextReject;
    });
    void nextPromise.catch(() => undefined);
    return nextPromise;
  };
  let promise = makePromise();

  return {
    wait: () => promise,
    resolve: () => {
      resolve?.();
      resolve = null;
      reject = null;
    },
    reject: (error: unknown) => {
      reject?.(error);
      resolve = null;
      reject = null;
    },
    reset: () => {
      promise = makePromise();
    },
  };
}

export function createEnvironmentConnection(
  input: EnvironmentConnectionInput,
): EnvironmentConnection {
  const environmentId = input.knownEnvironment.environmentId;

  if (!environmentId) {
    throw new Error(
      `Known environment ${input.knownEnvironment.label} is missing its environmentId.`,
    );
  }

  let disposed = false;
  const bootstrapGate = createBootstrapGate();
  const shouldObserveLifecycle = input.kind === "saved" || input.onWelcome !== undefined;
  const shouldObserveConfig = input.kind === "saved" || input.onConfigSnapshot !== undefined;

  const observeEnvironmentIdentity = (nextEnvironmentId: EnvironmentId, source: string) => {
    if (environmentId !== nextEnvironmentId) {
      throw new Error(
        `Environment connection ${environmentId} changed identity to ${nextEnvironmentId} via ${source}.`,
      );
    }
  };

  const unsubLifecycle = shouldObserveLifecycle
    ? input.client.server.subscribeLifecycle((event) => {
        if (disposed || event.type !== "welcome") {
          return;
        }

        observeEnvironmentIdentity(
          event.payload.environment.environmentId,
          "server lifecycle welcome",
        );
        input.onWelcome?.(event.payload);
      })
    : () => undefined;

  const unsubConfig = shouldObserveConfig
    ? input.client.server.subscribeConfig((event) => {
        if (disposed || event.type !== "snapshot") {
          return;
        }

        observeEnvironmentIdentity(
          event.config.environment.environmentId,
          "server config snapshot",
        );
        input.onConfigSnapshot?.(event.config);
      })
    : () => undefined;

  let lastSequence: number | undefined;
  let unsubShell: () => void = () => undefined;
  let snapshotRequest: Promise<void> | null = null;
  let shellGeneration = 0;
  let snapshotRetry = 0;
  let cancelSnapshotRetry: () => void = () => undefined;

  const loadSnapshot = (): Promise<void> => {
    if (snapshotRequest) return snapshotRequest;
    cancelSnapshotRetry();
    const generation = ++shellGeneration;
    unsubShell();
    lastSequence = undefined;
    bootstrapGate.reset();
    snapshotRequest = input
      .fetchShellSnapshot()
      .then((snapshot) => {
        snapshotRequest = null;
        if (disposed) return;
        snapshotRetry = 0;
        input.syncShellSnapshot(snapshot, environmentId);
        lastSequence = snapshot.snapshotSequence;
        unsubShell = input.client.orchestration.subscribeShell(
          (item) => {
            if (disposed || generation !== shellGeneration) return;
            if (item.kind === "resync") {
              shellGeneration++;
              unsubShell();
              queueMicrotask(() => {
                if (!disposed) void loadSnapshot().catch(() => undefined);
              });
              return;
            }
            if (item.kind === "snapshot") {
              input.syncShellSnapshot(item.snapshot, environmentId);
              lastSequence = item.snapshot.snapshotSequence;
              return;
            }
            if (item.sequence <= lastSequence!) return;
            input.applyShellEvent(item, environmentId);
            lastSequence = item.sequence;
          },
          {
            afterSequence: () => lastSequence!,
            onResubscribe: () => {
              if (!disposed) input.onShellResubscribe?.(environmentId);
            },
          },
        );
        bootstrapGate.resolve();
      })
      .catch((error: unknown) => {
        snapshotRequest = null;
        bootstrapGate.reject(error);
        if (!disposed) {
          const delay = getReconnectDelayMs(snapshotRetry++, {
            ...DEFAULT_RECONNECT_BACKOFF,
            maxRetries: null,
          });
          cancelSnapshotRetry = Effect.runCallback(Effect.sleep(delay!), {
            onExit: (exit) => {
              if (Exit.isSuccess(exit) && !disposed) void loadSnapshot().catch(() => undefined);
            },
          });
        }
        throw error;
      });
    return snapshotRequest;
  };

  void loadSnapshot().catch(() => undefined);

  const cleanup = () => {
    if (disposed) {
      return;
    }

    disposed = true;
    cancelSnapshotRetry();
    bootstrapGate.reject(new EnvironmentConnectionDisposedError(environmentId));
    unsubShell();
    unsubLifecycle();
    unsubConfig();
  };

  return {
    kind: input.kind,
    environmentId,
    knownEnvironment: input.knownEnvironment,
    client: input.client,
    ensureBootstrapped: () =>
      disposed
        ? Promise.reject(new EnvironmentConnectionDisposedError(environmentId))
        : bootstrapGate.wait(),
    reconnect: async () => {
      if (disposed) {
        throw new EnvironmentConnectionDisposedError(environmentId);
      }

      try {
        await input.client.reconnect();
        if (lastSequence === undefined) await loadSnapshot();
        await input.refreshMetadata?.();
        await bootstrapGate.wait();
      } catch (error) {
        bootstrapGate.reject(error);
        throw error;
      }
    },
    dispose: async () => {
      cleanup();
      await input.client.dispose();
    },
  };
}
